/**
 * UTS (Unified Template Service) API client (authenticated) — marketplace
 * deploy wizard (design spec §2.1, Phase 2).
 *
 * Powers the native deploy click-path:
 *   - GET  /api/cluster/uts/templates/{id}              → template definition
 *   - GET  /api/cluster/app-templates/{id}/wizard-definition (fallback)
 *   - POST /api/cluster/uts/instances                   → create draft instance
 *   - POST /api/cluster/uts/instances/{id}/deploy       → kick the deploy
 *   - POST /api/cluster/app-templates/{id}/manifest-preview → Review step
 *   - GET  /api/cluster/uts/instances/{id}/preflight-status → live orb polling
 *
 * Mirrors `cluster-api.js`: one axios instance off `envConfig`, best-effort
 * Bearer attach via the request interceptor, and the unified
 * `{success,message,data}` envelope unwrapped in `unwrap()`.
 *
 * Endpoint-auth note (verified against cluster-api): both
 * `uts/templates/{id}` AND `app-templates/{id}/wizard-definition` accept a
 * Bearer token (200 with token, 401 without). We PREFER `uts/templates/{id}`
 * because its per-component `fields[]` arrays carry the full field schema the
 * wizard renders, and it is documented as `@token_or_session_required`.
 */
import axios from 'axios'
import { attachOrgLockedHandler } from '@/services/org-locked'
import store from '@/store'
import { envConfig } from '@/utils/env-config'
import { readToken, ACCESS_TOKEN_KEY } from '@/utils/token-storage'

const BASE_URL = envConfig.VUE_APP_API_BASE_URL || envConfig.VITE_API_BASE_URL
let API_VERSION = envConfig.VUE_APP_API_VERSION || envConfig.VITE_API_VERSION || ''
API_VERSION = API_VERSION.replace(/['"]/g, '').trim()
if (API_VERSION === 'v1' || API_VERSION === '/v1' || API_VERSION === 'api/v1') {
  API_VERSION = ''
}

const utsAxios = axios.create({
  baseURL: API_VERSION ? `${BASE_URL}/api/${API_VERSION}` : `${BASE_URL}/api`,
  timeout: envConfig.VUE_APP_API_TIMEOUT || envConfig.VITE_API_TIMEOUT || 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

utsAxios.interceptors.request.use((config) => {
  const token = store.state.auth?.accessToken || readToken(ACCESS_TOKEN_KEY)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ── Transient-failure retry (idempotent GETs only) ─────────────────────────
// Two transient failure modes hit the app-detail page on dev.caucasus.systems
// and both are safe to retry for a GET:
//   1. `net::ERR_NETWORK_CHANGED` (axios ERR_NETWORK, NO HTTP response) — Chrome
//      aborts in-flight requests when the Mac's network stack changes (a
//      WireGuard/VPN re-handshake on the tunnel to site-b). curl over the same
//      path succeeds, so it is a client-side transport hiccup, not the server.
//   2. HTTP 502/503/504 — site-b's single-worker cluster-api intermittently
//      sheds a slow request (the ~12s `/full` aggregation) under concurrent
//      load. The same call answers 200 moments later.
// Retry GETs a couple of times with short backoff so one hiccup doesn't
// hard-fail the whole page ("Couldn't load this app"). A 4xx (a real client
// error) is NEVER retried; nor are non-GET (potentially non-idempotent)
// requests. If all retries fail, the manual Retry button remains the fallback.
const RETRYABLE_NETWORK_CODES = new Set(['ERR_NETWORK'])
const RETRYABLE_STATUS = new Set([502, 503, 504])
const MAX_NETWORK_RETRIES = 2
const RETRY_BASE_MS = 400

utsAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config
    const isGet = (config?.method || 'get').toLowerCase() === 'get'
    const status = error?.response?.status
    const isTransient =
      (!error?.response && RETRYABLE_NETWORK_CODES.has(error?.code)) ||
      (status !== undefined && RETRYABLE_STATUS.has(status))
    if (config && isGet && isTransient) {
      config.__netRetry = (config.__netRetry || 0) + 1
      if (config.__netRetry <= MAX_NETWORK_RETRIES) {
        const delay = RETRY_BASE_MS * Math.pow(2, config.__netRetry - 1)
        await new Promise((resolve) => setTimeout(resolve, delay))
        return utsAxios(config)
      }
    }
    return Promise.reject(error)
  }
)

/**
 * Connection config for the cluster-api pod-shell Socket.IO endpoint, derived
 * from the same base + token the axios client uses. `url` is the API origin
 * (or undefined → same origin, the common HAProxy-routed case); `path` is the
 * cluster-api custom Socket.IO path; `token` is the live JWT for `auth`.
 * Used by the per-pod Console (Execute / shell) modal.
 */
export function getPodShellSocketConfig() {
  const token = store.state.auth?.accessToken || readToken(ACCESS_TOKEN_KEY)
  return {
    url: BASE_URL || undefined,
    path: '/api/cluster/socket.io/',
    token
  }
}

/**
 * Typed error carrying the parsed quota signal from a 402 deploy response so
 * the wizard's Step 4 can re-open the QuotaGate modal (defense-in-depth).
 */
// The one rule for "the browser gave up" vs "the server refused" — see
// src/utils/apply-state.js. Re-exported so callers can import it from here.
export { isApplyStillRunning, APPLY_TIMEOUT_MS } from '@/utils/apply-state'
import { APPLY_TIMEOUT_MS } from '@/utils/apply-state'

export class QuotaError extends Error {
  constructor(message, payload) {
    super(message || 'Quota exceeded')
    this.name = 'QuotaError'
    this.quota = true
    // payload typically { error|reason, suggested_plan_id, ... }
    this.payload = payload || {}
  }
}

/**
 * Typed error for the PUT /uts/instances/{id} OCC (optimistic concurrency)
 * conflict (HTTP 409). The body carries the actual `current` updated_at so
 * the edit wizard can refresh and re-arm a retry.
 *
 *   { current: "2026-05-30T12:34:56Z" }  → conflict.current
 *
 * Phase 4 — Edit wizard. The wizard catches ConflictError, surfaces a
 * non-destructive banner (refresh + choose to discard or keep local edits),
 * and never silently overwrites the server state.
 */
export class ConflictError extends Error {
  constructor(message, payload) {
    super(message || 'This app was updated elsewhere')
    this.name = 'ConflictError'
    this.conflict = true
    this.payload = payload || {}
    // The freshest updated_at the server knows about (string or null).
    this.current = payload?.current || payload?.data?.current || null
  }
}

function unwrap(response) {
  const body = response?.data
  if (body && typeof body === 'object' && 'success' in body) {
    if (body.success === false) {
      const err = new Error(body.message || 'Request failed')
      err.response = response
      throw err
    }
    return body.data
  }
  return body
}

export const utsApi = {
  /**
   * Fetch a template definition (components + flat `fields[]` schema) used to
   * render the Configuration step. PREFERRED over wizard-definition.
   */
  async getTemplate(id, clusterName) {
    // `cluster` is optional and the schema is served without it. A field's
    // `default_from` is a per-cluster indirection (the cluster's Platform
    // Defaults panel), so without a target the server resolves the catalog's
    // own `default_fallback` instead — which is why the control is never born
    // blank either way. Pass it once the deploy target is known so an
    // operator-configured cluster default wins.
    const qs = clusterName
      ? `?cluster=${encodeURIComponent(clusterName)}`
      : ''
    const response = await utsAxios.get(
      `/cluster/uts/templates/${encodeURIComponent(id)}${qs}`
    )
    return unwrap(response)
  },

  /**
   * Fallback field/step schema. Token-compatible too, but its component list
   * does not embed per-field arrays the same way `getTemplate` does — keep it
   * as a backup only.
   */
  async getWizardDefinition(id, clusterName) {
    const qs = clusterName
      ? `?cluster=${encodeURIComponent(clusterName)}`
      : ''
    const response = await utsAxios.get(
      `/cluster/app-templates/${encodeURIComponent(id)}/wizard-definition${qs}`
    )
    return unwrap(response)
  },

  /**
   * Render the manifests for the chosen values (Review step).
   * @param {string} id template id
   * @param {{ namespace: string, values: object, addons?: object }} payload
   */
  async manifestPreview(id, payload) {
    const response = await utsAxios.post(
      `/cluster/app-templates/${encodeURIComponent(id)}/manifest-preview`,
      payload
    )
    return unwrap(response)
  },

  /**
   * Create a draft UTS instance. Always `origin:"catalog"` for the marketplace.
   * @param {object} payload { name, origin, template_id, template_version?,
   *   target:{cluster_name,namespace}, values, addons? }
   */
  /**
   * GitLab Import explorer (cluster-api 2.89.0). The listing is scoped to the
   * caller's own GitLab group SERVER-side; this client never filters it.
   * @param {{serverId?:string, search?:string, namespace?:string, page?:number, perPage?:number, signal?:AbortSignal}} q
   */
  async listMyGitlabProjects({ serverId, search, namespace, page = 1, perPage = 20, signal } = {}) {
    const params = { page, per_page: perPage }
    if (serverId) params.gitlab_server_id = serverId
    if (search) params.search = search
    if (namespace) params.namespace = namespace
    const response = await utsAxios.get('/cluster/uts/gitlab/my-projects', { params, signal })
    return unwrap(response)
  },

  /** Branches of one project in the caller's own group. */
  async listMyGitlabProjectBranches(projectId, { serverId } = {}) {
    const params = serverId ? { gitlab_server_id: serverId } : {}
    const response = await utsAxios.get(
      `/cluster/uts/gitlab/my-projects/${encodeURIComponent(projectId)}/branches`, { params }
    )
    return unwrap(response)
  },

  /** Read-only probe: app type, Dockerfile, detected port, workspace config. */
  async probeGitlabProject({ serverId, projectId, branch }) {
    const response = await utsAxios.post('/cluster/uts/gitlab/probe', {
      gitlab_server_id: serverId,
      gitlab_project_id: projectId,
      target_branch: branch
    })
    return unwrap(response)
  },

  async createInstance(payload) {
    const body = { origin: 'catalog', ...payload }
    // Catalog maturity V2: this used to send `X-Deploy-Source: customer_portal`
    // so the ledger could split operator vs customer successes. cluster-api
    // stopped reading that header on 2026-09-01 and now derives the source from
    // the authenticated caller — a client header is not evidence, since a
    // dashboard user could send `customer_portal` and manufacture the customer
    // half of the catalogue maturity gate. Sending it would only imply a
    // guarantee this client cannot make.
    const response = await utsAxios.post('/cluster/uts/instances', body)
    return unwrap(response)
  },

  /**
   * Kick the deploy for a draft instance. A 402 (quota raced) is translated to
   * a typed QuotaError so the wizard re-opens the QuotaGate modal.
   */
  async deployInstance(id) {
    try {
      // No `X-Deploy-Source` header — see `createInstance` above. cluster-api
      // attributes the deploy from the authenticated caller, so a customer's
      // deploy still reaches `user_success_count`; it just is not this client
      // that says so.
      // A deploy renders, validates and applies every manifest before it
      // answers; on a busy cluster that is well over the 30s axios default,
      // and a client timeout was being shown to the customer as "Apply failed"
      // while the deploy carried on and succeeded. Same treatment as the
      // ai-diagnosis call below. `isApplyStillRunning` lets the caller tell a
      // timeout from a real refusal.
      const response = await utsAxios.post(
        `/cluster/uts/instances/${encodeURIComponent(id)}/deploy`,
        {},
        { timeout: APPLY_TIMEOUT_MS }
      )
      return unwrap(response)
    } catch (error) {
      if (error?.response?.status === 402) {
        const body = error.response.data || {}
        throw new QuotaError(body.message || body.error || 'Quota exceeded', {
          reason: body.reason || body.error || 'quota_exceeded',
          suggested_plan_id: body.suggested_plan_id ?? body?.data?.suggested_plan_id ?? null,
          ...(body.data || {})
        })
      }
      throw error
    }
  },

  /** Live preflight status for the deploy orb. `{status, light, ...}`. */
  async getPreflightStatus(id) {
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/preflight-status`
    )
    return unwrap(response)
  },

  // ───────────────────────────── Phase 3 (My Apps) ─────────────────────────
  //
  // Verified against the live cluster-api (org-1 token, 2026-05). Notable
  // shape facts the views rely on:
  //   - Instance id field is `_id` (ti_…), not `id`.
  //   - created_at / updated_at live under `audit`, not top-level.
  //   - There is NO top-level pod list; per-branch pod counts live in
  //     `reconciled_branches[branch].components[name].pod_summary`.
  //   - `/pods` and `/preflight*` 404 for instances that never ran a
  //     preflight pipeline (catalog/adopted apps) — callers MUST treat 404
  //     as "no data", not as a hard error.
  //   - delete-preview REQUIRES a `mode` query param
  //     (`branch_only` | `full_application`); `branch_only` also needs `branch`.
  //   - connection-info is keyed by cluster + namespace, not instance id.

  /**
   * Org-scoped instance list (My Apps table). The backend scopes by the
   * caller's token; pass `organization_id` to narrow further. Filters:
   * `origin, cluster_name, namespace, deploy_state, organization_id, limit`.
   * @param {object} [params]
   * @returns {Promise<Array>} array of instance objects.
   */
  async listInstances(params = {}) {
    const clean = {}
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') clean[k] = v
    })
    const response = await utsAxios.get('/cluster/uts/instances', { params: clean })
    return unwrap(response) || []
  },

  /** Full instance detail (My App detail Overview / Edit prefill). */
  async getInstance(id) {
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}`
    )
    return unwrap(response)
  },

  /**
   * The `/general` composite for one instance — used by the Dev Environments
   * create flow (P8) for its `dev_mode` OFFER block:
   *   - `dev_mode: null`            → dev mode is not offered for this instance
   *   - `{ enabled:false, available, reason_code, workload_kind, reason,
   *        dev_run_available, dev_workspace_available,
   *        defaults:{ git_url, git_branch, container, containers[], mount_path,
   *                   dev_env, workload_name }, post_clone }`
   *   - `{ enabled:true, config:{…}, live }` → already in dev mode (adoptable)
   * Returns the whole composite; callers read `.dev_mode`.
   */
  async getInstanceGeneral(id) {
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/general`
    )
    return unwrap(response)
  },

  /**
   * Comprehensive permission-filtered instance payload — ONE call powering the
   * rich Overview + the edit-wizard hydration. The backend aggregates the base
   * doc (secret-masked values), the /general composite (quick stats + live
   * pods/ingresses/services + gitlab identity), resolved .env/.denv (secrets
   * masked unless the caller holds secret:viewer), the selectable GitLab server
   * list, a snapshot summary, and a `permissions` block.
   *
   * → { instance:{…,values,values_masked}, general:{…,runtime,live_pod_summary},
   *     config:{ env:{content,source,masked}, denv:{content,source} },
   *     gitlab:{ current_server_id, current_project_id, servers:[…] },
   *     snapshots:{count,latest}, permissions:{can_view,can_edit,can_view_secrets} }
   */
  async getInstanceFull(id, branch, { refresh = false } = {}) {
    const params = {}
    if (branch) params.branch = branch
    // refresh=1 bypasses the server's short-TTL Overview live-runtime cache
    // (Refresh button → always-fresh live pods/status).
    if (refresh) params.refresh = 1
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/full`,
      { params }
    )
    return unwrap(response)
  },

  /**
   * Cached AI root-cause diagnosis for an instance (the failed/degraded-app
   * banner). Returns the diagnosis sub-doc { status, severity, summary,
   * root_cause, recommendations[], model, generated_at } or null when none
   * exists (healthy app / auto-sweep never ran).
   */
  async getAiDiagnosis(id) {
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/ai-diagnosis`
    )
    return unwrap(response)
  },

  /**
   * Run (or return the cached) AI diagnosis on demand — the banner's
   * Analyze / Re-analyze button. Read-only against the cluster; the result is
   * persisted server-side. `force` (default true) re-runs even when cached.
   */
  async runAiDiagnosis(id, { force = true, model } = {}) {
    // `model` (from the modal dropdown) runs this analysis on that model AND
    // saves it as the workload's default. Omit to keep the current default.
    const body = { force }
    if (model !== undefined) body.model = model
    // A fresh LLM run (Mistral 7B on the Jetson, cold model load + generation)
    // takes 30–90s; ai-api's own timeout is 90s. The default 30s axios timeout
    // would abort a legitimate analysis, so give this call a 120s ceiling.
    const response = await utsAxios.post(
      `/cluster/uts/instances/${encodeURIComponent(id)}/ai-diagnosis`,
      body,
      { timeout: 120000 }
    )
    return unwrap(response)
  },

  // ───────────────────────── Detail tabs (logs / config / resources) ───────
  // All instance-scoped and gated by view-access to the instance, so they
  // work for any org member with workload:viewer. env/denv are cs-application
  // only (other templates return source:'none' with empty content).

  /** `.env` file content from the config repo. → {branch, source, content, canonical}. */
  async getEnvPreview(id, branch) {
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/env-preview`,
      { params: branch ? { branch } : {} }
    )
    return unwrap(response)
  },

  /** `.denv` file content from the config repo. → {branch, source, content, canonical}. */
  async getDenvPreview(id, branch) {
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/denv-preview`,
      { params: branch ? { branch } : {} }
    )
    return unwrap(response)
  },

  /**
   * Config compliance — the .env recommendations (missing / mismatched keys vs
   * the canonical .env.example). → { ok, items:[{key, state, severity,
   * recommendation:{value}, apply_fix_safe, actual?, expected?}] }. View-gated.
   */
  async getCompliance(id) {
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/compliance`
    )
    return unwrap(response)
  },

  /**
   * Persist an edited .env to the config repo (NO deploy). Gated by
   * secret:editor (or owner/admin) on the instance. → { action:'create'|'update',
   * commit_sha, web_url, path, ... }.
   * @param {string} id
   * @param {{ branch: string, content: string, commit_message?: string }} payload
   */
  async saveEnv(id, payload = {}) {
    const response = await utsAxios.post(
      `/cluster/uts/instances/${encodeURIComponent(id)}/env-save`,
      payload
    )
    return unwrap(response)
  },

  /**
   * Trigger a fresh deploy pipeline on the workload's GitLab project (picks up
   * the freshly-saved .env). Gated by secret:editor (matches saveEnv). →
   * { mode, pipeline:{ id, web_url } }.
   * @param {string} id
   * @param {{ branch: string, message?: string }} payload
   */
  async triggerDeploy(id, payload = {}) {
    const response = await utsAxios.post(
      `/cluster/uts/instances/${encodeURIComponent(id)}/trigger-deploy`,
      payload
    )
    return unwrap(response)
  },

  /**
   * Live K8s pods grouped by branch (works for adopted apps, unlike the
   * preflight `/pods` which 404s). → {branches:[{branch, namespace, pods:[
   * {name, containers:[{name,...}], ...}]}]}.
   */
  async getClusterPods(id) {
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/cluster-pods`
    )
    return unwrap(response)
  },

  /** Live K8s resources for the instance. → {branch, secrets[], configmaps[], pvcs[]}. */
  async getResources(id, branch) {
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/resources`,
      { params: branch ? { branch } : {} }
    )
    return unwrap(response)
  },

  /**
   * A single Secret's data for inline reveal. Values are base64 and only
   * present when the caller holds secret:viewer (`reveal_allowed`). Otherwise
   * `data_b64` is empty and `value_lengths` carries the decoded byte counts.
   * → { name, type, data_b64:{key:b64}, data_keys[], value_lengths:{key:n},
   *     reveal_allowed }.
   */
  async getSecretPreview(id, name, branch) {
    const params = { name }
    if (branch) params.branch = branch
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/secret-preview`,
      { params }
    )
    return unwrap(response)
  },

  /**
   * A single ConfigMap's content. → { name, data:{key:value}, labels,
   * annotations, binary_data_keys[] }. Gated by view-access to the instance.
   */
  async getConfigmapPreview(id, name, branch) {
    const params = { name }
    if (branch) params.branch = branch
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/configmap-preview`,
      { params }
    )
    return unwrap(response)
  },

  /**
   * Ingresses in the instance's namespace.
   * → {branch, namespace, cluster, ingresses:[{name, hosts[], tls, address,
   *    ingress_class, rules:[{host, path, path_type, service, port}]}]}.
   */
  async getIngressList(id, branch) {
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/ingress-list`,
      { params: branch ? { branch } : {} }
    )
    return unwrap(response)
  },

  /**
   * Latest GitLab pipeline per branch, with its jobs.
   *
   * → {instance_id, gitlab_server_id, gitlab_project_id, include_jobs,
   *    branches:{ "<branch>": {
   *      pipeline:{id, iid, status, web_url, ref, sha, created_at,
   *                started_at, finished_at, duration} | null,
   *      jobs:[{id, name, stage, status, web_url, started_at, finished_at,
   *             duration, queued_duration, allow_failure}],
   *      jobs_unavailable?:"<reason>", error?:"<reason>" }}}
   *
   * Also `{unavailable:true, reason}` at the top level when the instance has
   * no GitLab project linked. The backend is wall-clock bounded and never
   * 500s, so every failure arrives as one of those strings — render it, do
   * not retry forever. Shorter client timeout than the axios default: this
   * feeds a card, not a workflow, and a slow GitLab must not hold the page.
   */
  async getGitlabPipelines(id, { includeJobs = true, branch } = {}) {
    const params = {}
    if (includeJobs) params.include_jobs = 1
    if (branch) params.branch = branch
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/gitlab-pipelines`,
      { params, timeout: 20000 }
    )
    return unwrap(response)
  },

  /**
   * Live Resources gauge summary (single round-trip for all 5 gauges).
   * → { values:{cpu,memory,net_rx,net_tx,storage}, unavailable:{},
   *     live_resources:{cpu_limit_cores,memory_limit_bytes,ready_pods,desired_pods},
   *     transport, cluster_name, namespace, workload_name }.
   */
  async getMonitoringLiveSummary(id, { workloadType = 'all' } = {}) {
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/monitoring/live-summary`,
      { params: { workload_type: workloadType } }
    )
    return unwrap(response)
  },

  /**
   * Native Prometheus time-series for one metric/range.
   * metric ∈ cpu|memory|net_rx|net_tx|storage · range ∈ 15m|1h|6h|24h|7d.
   * → { metric, range_s, step_s, start, end, series:[{name, points:[[ts,val|null]]}] }.
   */
  async getMonitoringMetrics(id, { metric, range = '1h', workloadType = 'all' } = {}) {
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/monitoring/metrics`,
      { params: { metric, range, workload_type: workloadType } }
    )
    return unwrap(response)
  },

  // ──────────────────────── Volume file browser (volume:viewer) ────────────
  /**
   * List files/dirs on a PVC the instance mounts.
   * → { current_path, pv_name, pvc_name, breadcrumbs:[{name,path}],
   *     items:[{name,path,is_directory,type,size,size_formatted,permissions,
   *     modified_formatted}], item_count, directory_count, file_count }.
   */
  async browseVolumeFiles(id, { pvc, path = '/', branch } = {}) {
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/volumes/files`,
      { params: { pvc, path, ...(branch ? { branch } : {}) } }
    )
    return unwrap(response)
  },

  /** Preview a text/image file → { content | data_url, mime, ... }. */
  async previewVolumeFile(id, { pvc, path, branch } = {}) {
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/volumes/files/preview`,
      { params: { pvc, path, ...(branch ? { branch } : {}) } }
    )
    return unwrap(response)
  },

  /** Download a file → Blob (caller triggers the save). */
  async downloadVolumeFile(id, { pvc, path, branch } = {}) {
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/volumes/files/download`,
      { params: { pvc, path, ...(branch ? { branch } : {}) }, responseType: 'blob' }
    )
    return response.data
  },

  // ───────────────────────────── Adoption (All Workloads tab) ──────────────
  // Adopt a raw K8s workload into a managed UTS instance. The commit endpoint
  // derives owner.organization_id from the caller's token automatically, so an
  // org member's adoptions land in their org and appear in the instances list.

  /**
   * Unmanaged + adopted workloads in a cluster (optionally one namespace).
   * → {cluster, namespace, workloads:[{kind, name, namespace, status,
   *    instance_id, managed_by, ...}]}.  status ∈ unmanaged | adopted | …
   */
  async getUnmanagedWorkloads(cluster, namespace) {
    const params = { cluster }
    if (namespace) params.namespace = namespace
    const response = await utsAxios.get('/cluster/uts/adoption/unmanaged', { params })
    return unwrap(response)
  },

  /** Recognize a workload (proposed template / confidence). Non-mutating. */
  async inspectWorkload({ cluster, namespace, workloadKind, workloadName }) {
    const response = await utsAxios.post('/cluster/uts/adoption/inspect', {
      cluster, namespace, workload_kind: workloadKind, workload_name: workloadName
    })
    return unwrap(response)
  },

  /** Commit adoption → creates the instance (owner org from token). → {created, instance_id, …}. */
  async commitAdoption({ cluster, namespace, workloadKind, workloadName, targetAppType }) {
    const body = {
      cluster, namespace, workload_kind: workloadKind, workload_name: workloadName
    }
    if (targetAppType) body.target_app_type = targetAppType
    const response = await utsAxios.post('/cluster/uts/adoption/commit', body)
    return unwrap(response)
  },

  /**
   * Container logs for one pod (generic cluster endpoint).
   * → {pod_name, namespace, container, logs, previous}.
   */
  async getPodLogs({ cluster, namespace, pod, container, tail = 200, previous = false }) {
    const params = { cluster, namespace, pod_name: pod, tail_lines: tail }
    if (container) params.container = container
    if (previous) params.previous = true
    const response = await utsAxios.get('/cluster/pod/logs', { params })
    return unwrap(response)
  },

  /**
   * Logs for a pod belonging to a specific UTS instance — workload:viewer
   * gated and instance-scoped (the cluster + namespace are resolved server-side
   * from the instance, NOT trusted from the client). Powers the per-pod Console
   * (logs) modal on the My App detail page.
   *
   * `cluster` is OPTIONAL and used only for cross-cluster SOURCE pods (a
   * multi-cluster component's collector/buffer legs run on another cluster). The
   * backend honours it ONLY when it is one of THIS instance's own source
   * clusters (else 403) — it is NOT a free cluster selector. Destination (local)
   * pods MUST call without it, so their behaviour is unchanged.
   * → {pod_name, namespace, container, cluster, logs, previous, tail_lines}.
   */
  async getInstancePodLogs(id, { pod, container, namespace, cluster, tail = 500, previous = false } = {}) {
    const params = { pod, tail }
    if (container) params.container = container
    if (namespace) params.namespace = namespace
    if (cluster) params.cluster = cluster
    if (previous) params.previous = true
    const response = await utsAxios.get(`/cluster/uts/instances/${id}/pod-logs`, { params })
    return unwrap(response)
  },

  /**
   * Real owned assets for a LICENSED org — the datacenters → clusters →
   * namespaces → workloads tree plus aggregate totals + capacity. Member-
   * gated (the caller must be a member of the org). Pass `{capacity:false}`
   * to skip the slow node-metrics pass (counts are always real; capacity
   * cpu/mem may be 0 with metrics_available:false when nodes are
   * unreachable).
   *
   * @param {number|string} orgId
   * @param {{ capacity?: boolean }} [opts]
   * @returns {Promise<object|null>} the data object
   *   {org_id, datacenters[], totals:{datacenters,clusters,namespaces,
   *    workloads,capacity}} or null on failure.
   */
  async getMyOrgAssets(orgId, { capacity = true } = {}) {
    const params = { organization_id: orgId }
    if (!capacity) params.capacity = 0
    const response = await utsAxios.get('/cluster/my/org-assets', { params })
    return unwrap(response) || null
  },

  /**
   * Per-storage-class capacity + usage for the org's owned clusters, merged
   * across datacenter (Phase 2a telemetry). Member-gated like org-assets.
   *
   * @param {number|string} orgId
   * @returns {Promise<object|null>} the data object
   *   {org_id, cluster_count, metrics_available, datacenters[],
   *    totals:{by_class:{hostpath,nfs,other:{capacity_gib,used_gib}}},
   *    sources:{pvc_gib, gitlab_gib, files_gib}} or null on failure.
   */
  // cluster-api 2.98.0 — the caller's own application repositories on the
  // customer GitLab (<group>/apps only; the config repo is never listed).
  async getMyAppRepos(orgId) {
    const response = await utsAxios.get('/cluster/my/app-repos', {
      params: { organization_id: orgId }
    })
    return unwrap(response) || null
  },

  // cluster-api 2.98.0 — the caller's own configuration repository, read-only.
  async getMyConfigRepo(orgId) {
    const response = await utsAxios.get('/cluster/my/config-repo', {
      params: { organization_id: orgId }
    })
    return unwrap(response) || null
  },

  async getMyStorage(orgId) {
    const response = await utsAxios.get('/cluster/my/storage', {
      params: { organization_id: orgId }
    })
    return unwrap(response) || null
  },

  /**
   * License-vs-hosted summary for the org (SUBSCRIPTION_PLAN_LICENSE_MODEL S5).
   * For a LICENSE org returns entitlements + per-node usage + evaluation so the
   * UI can show managed-nodes/features/seats instead of quota bars; for a
   * HOSTED org returns just {plan_kind:'hosted'} and the UI keeps the bars.
   *
   * @param {number|string} orgId
   * @returns {Promise<object|null>} {org_id, plan_kind, entitlements:{max_nodes,
   *   seats, features:string[], support_tier}|null, nodes:{total_workers,
   *   total_nodes, clusters:[], metrics_available, unreachable}|null,
   *   evaluation:{within_limits, used_nodes, max_nodes, nodes_over_by}|null} | null
   */
  async getMyLicenseSummary(orgId) {
    const response = await utsAxios.get('/cluster/my/license-summary', {
      params: { organization_id: orgId }
    })
    return unwrap(response) || null
  },

  /**
   * The org/plan tier + quota + current usage for the caller (resolved from
   * the org's active subscription). Reuses the existing quota-check endpoint.
   *
   * @returns {Promise<object|null>} {tier, limits, limits_numeric:{cpu_cores,
   *   memory_gib, storage_gib, max_workloads}, usage:{cpu_cores, memory_gib,
   *   storage_gib, workloads}, trial, allowed, reason, suggested_plan_id} | null
   */
  async getMyQuota() {
    const response = await utsAxios.get('/cluster/uts/quota/check')
    return unwrap(response) || null
  },

  /**
   * Per-member tier ALLOCATIONS for an org + the tiers available to allocate
   * (tier-as-asset delegation). Org-admin gated server-side.
   *
   * @param {number|string} orgId
   * @returns {Promise<object|null>} {org_id, allocations:{str(user_id):tier_key},
   *   available_tiers:string[]} | null
   */
  async getMyTierGrants(orgId) {
    const r = await utsAxios.get('/cluster/my/tier-grants', {
      params: { organization_id: orgId }
    })
    return unwrap(r) || null
  },

  /**
   * Allocate a tier to a member (or REMOVE it with tierKey=null → the member
   * reverts to inheriting the org's own plan tier). Org-admin gated.
   *
   * @param {number|string} orgId
   * @param {number|string} userId
   * @param {string|null} tierKey
   * @returns {Promise<object|null>} the updated allocations doc
   */
  async putMyTierGrant(orgId, userId, tierKey) {
    const r = await utsAxios.put('/cluster/my/tier-grants', {
      organization_id: orgId, user_id: userId, tier_key: tierKey
    })
    return unwrap(r) || null
  },

  // -------------------------------------------------------------------
  // Workspace Phase 3 — tenant namespace self-service (org-scoped).
  //
  // `organization_id` is sent on EVERY call: cluster-api's
  // @require_permission(scope_param='organization_id') checks the grant in
  // that org and the route refuses any value that is not the caller's
  // active org (super-admins excepted).
  // -------------------------------------------------------------------

  /**
   * The org's namespaces + envelope + what the caller may do.
   * @returns {Promise<object|null>} {organization_id, tier, org_slug,
   *   platform_subdomains[], placement, envelope, namespaces[], caller}
   */
  async getMyNamespaces(orgId) {
    const r = await utsAxios.get('/cluster/uts/namespaces', {
      params: { organization_id: orgId }
    })
    return unwrap(r) || null
  },

  /**
   * The caller's grant-filtered deploy targets (Workspace Phase 4) — what
   * the Deploy wizard's target picker lists, and exactly what cluster-api
   * will accept as `target` on create/deploy (same server-side resolver).
   * `organization_id` is bound to the caller's active org server-side.
   * @returns {Promise<object|null>} {organization_id, tier, archetype,
   *   provisioning, targets[], default_target, refused[], workloads}
   */
  async getDeployTargets(orgId) {
    const params = {}
    if (orgId) params.organization_id = orgId
    const r = await utsAxios.get('/cluster/uts/deploy-targets', { params })
    return unwrap(r) || null
  },

  /**
   * Is this host already claimed anywhere on this site? Backs the Domains
   * Configuration cards' "already in use" line. Since cluster-api 2.79.0 the
   * server checks every cluster the site manages (vClusters included), every
   * app record of any organisation and dns-manager's records for the zone —
   * `cluster` is no longer the scope. It answers only what the caller may
   * know: `{host, checked, in_use, same_app, own[], others_count, sources,
   * partial}` — `own` = claims proven to be the caller's app (`namespace` +
   * `app` / `instanceId` + their organisation; a redeploy, not a conflict);
   * `others_count` counts everyone else's claims, never naming them.
   * `checked: false` means the cluster could not be scanned — show nothing.
   * @returns {Promise<object|null>}
   */
  async checkHostname({ cluster, host, namespace, app, instanceId } = {}, { signal } = {}) {
    const params = { host }
    if (cluster) params.cluster = cluster
    if (namespace) params.namespace = namespace
    if (app) params.app = app
    if (instanceId) params.instance_id = instanceId
    const r = await utsAxios.get('/cluster/uts/hostname-check', { params, signal, timeout: 20000 })
    return unwrap(r) || null
  },

  /**
   * GET /cluster/uts/domain-options (cluster-api 2.79.0) — what a domain card
   * may offer this caller on this site: `{platform_domains[], own_domains[
   * {domain, status, verified}], own_domains_readable, operator,
   * reserved_labels[], add_domain_path}`. Null when the call fails.
   */
  async getDomainOptions() {
    try {
      const r = await utsAxios.get('/cluster/uts/domain-options', { timeout: 15000 })
      return unwrap(r) || null
    } catch (e) {
      return null
    }
  },

  /**
   * GET /cluster/cs-application/<data_source>/instances — the caller's OWN
   * database / RabbitMQ / Redis / Elasticsearch instances on a cluster
   * (cluster-api 2.84.1 filters by namespace ownership server-side). `request`
   * is `pickerRequest()`'s `{path, params}`. Throws on failure so the picker
   * can say so; an empty list is a real answer.
   */
  async listPickerInstances(request, { signal } = {}) {
    if (!request || !request.path) return []
    const r = await utsAxios.get(request.path, { params: request.params, signal, timeout: 45000 })
    const data = unwrap(r)
    return Array.isArray(data) ? data : []
  },

  /**
   * The caller's database naming prefix (org slug or personal slug) plus
   * per-engine limits — cluster-api 2.89.0 `GET …/database/naming`.
   */
  async getDatabaseNaming({ signal } = {}) {
    const r = await utsAxios.get('/cluster/cs-application/database/naming', { signal, timeout: 20000 })
    return unwrap(r) || {}
  },

  /**
   * "Create database and user" on one of the caller's own instances
   * (cluster-api 2.89.0). The body carries the instance identity, the names
   * and the password; never an admin login or a host — the server reads
   * those itself. Errors are thrown with the server's plain-words message.
   */
  async provisionDatabase(body) {
    try {
      const r = await utsAxios.post('/cluster/cs-application/database/provision', body, { timeout: 60000 })
      return unwrap(r) || {}
    } catch (e) {
      const resp = e && e.response
      const err = new Error((resp && resp.data && resp.data.message) || 'Could not create the database right now.')
      err.status = resp && resp.status
      err.code = resp && resp.data && (resp.data.error_code || resp.data.error)
      throw err
    }
  },

  /** Org-scoped requests (admins see all, others their own). */
  async getNamespaceRequests(orgId, { status } = {}) {
    const params = { organization_id: orgId }
    if (status) params.status = status
    const r = await utsAxios.get('/cluster/uts/namespaces/requests', { params })
    const data = unwrap(r)
    return Array.isArray(data) ? data : []
  },

  /**
   * Developer: opens a pending request. Admin: creates the namespace now.
   * 400 invalid name · 402 cap reached · 403 role · 409 limit / exists.
   */
  async requestNamespace(orgId, { name, reason } = {}) {
    const r = await utsAxios.post('/cluster/uts/namespaces/request', {
      organization_id: orgId, name, reason: reason || undefined
    })
    return unwrap(r) || null
  },

  async approveNamespaceRequest(orgId, requestId) {
    const r = await utsAxios.post(`/cluster/uts/namespaces/requests/${encodeURIComponent(requestId)}/approve`, {
      organization_id: orgId
    })
    return unwrap(r) || null
  },

  /** Admin → denied; the requester's own call → withdrawn. */
  async rejectNamespaceRequest(orgId, requestId, reason) {
    const r = await utsAxios.post(`/cluster/uts/namespaces/requests/${encodeURIComponent(requestId)}/reject`, {
      organization_id: orgId, reason: reason || undefined
    })
    return unwrap(r) || null
  },

  /**
   * Org collaboration connections (Integrations view — SOUTH plane). Lists
   * the active org's Slack / Jira / Active-Directory destinations a
   * super-admin set up. Member-gated (the caller passes via active-org
   * membership). The payload is already public-safe — `to_public` strips
   * every secret server-side, so the UI renders only non-secret fields
   * (provider, name, environment, status, auth.type + has_* flags).
   *
   * @param {number|string} orgId
   * @returns {Promise<{org_id?, connections: Array}>} never null — defaults
   *   to `{ connections: [] }` so the view shows its empty state, never a
   *   blank page.
   */
  async getMyCollabConnections(orgId) {
    const r = await utsAxios.get('/cluster/my/collab-connections', {
      params: { organization_id: orgId }
    })
    return unwrap(r) || { connections: [] }
  },

  /**
   * The scope_path clearances cleared for a single collaboration connection
   * destination — i.e. the org/cluster/namespace scopes whose data may be
   * shared to this destination. Lazily fetched when a connection card is
   * expanded. Read-only.
   *
   * @param {string} connectionId
   * @param {number|string} orgId
   * @returns {Promise<{connection_id?, provider?, destination?, clearances: Array}>}
   *   never null — defaults to `{ clearances: [] }`.
   */
  async getMyCollabClearances(connectionId, orgId) {
    const r = await utsAxios.get(
      `/cluster/my/collab-connections/${encodeURIComponent(connectionId)}/clearances`,
      { params: { organization_id: orgId } }
    )
    return unwrap(r) || { clearances: [] }
  },

  /**
   * Per-org + per-cs-application deploy governance (SOUTH plane, org-admin).
   * Reads the org's 4-eyes production-deploy approval policy: the per-org
   * default (`default_require_approval`: true | false | null=inherit baseline),
   * the per-application overrides (`app_overrides`: { app_key: bool }), plus the
   * platform availability context (`platform.config_repo_per_env_enabled` — the
   * prerequisite; `platform.prod_deploy_approval_baseline` — the fallback the
   * org inherits when its default is null).
   *
   * Org-admin-gated server-side (403 otherwise) — the caller must hold a
   * `deploy_governance` grant on the org (or be super-admin).
   *
   * @param {number|string} orgId
   * @returns {Promise<object|null>}
   */
  async getMyDeployGovernance(orgId) {
    const r = await utsAxios.get('/cluster/my/deploy-governance', {
      params: { organization_id: orgId }
    })
    return unwrap(r) || null
  },

  /**
   * Update the org's deploy governance. Body may carry EITHER or BOTH:
   *   • `default_require_approval`: true | false | null   (the org blanket default)
   *   • `app_override`: { app_key, require_approval }      (set bool / remove with null)
   * Returns the updated governance doc (+ platform context).
   *
   * @param {number|string} orgId
   * @param {{default_require_approval?: (boolean|null), app_override?: {app_key:string, require_approval:(boolean|null)}}} payload
   * @returns {Promise<object|null>}
   */
  async putMyDeployGovernance(orgId, payload = {}) {
    const r = await utsAxios.put(
      '/cluster/my/deploy-governance',
      { organization_id: orgId, ...payload }
    )
    return unwrap(r) || null
  },

  /**
   * Update an existing instance's editable surface. Uses optimistic
   * concurrency: pass the `expected_updated_at` captured at load time;
   * the backend returns 409 if another writer changed the instance in the
   * meantime.
   *
   * A 409 is translated to a typed ConflictError carrying `current` (the
   * actual server-side updated_at) so the edit wizard can refresh and
   * re-arm a retry without silently overwriting.
   *
   * A 402 is translated to a QuotaError (defense-in-depth, mirrors deploy)
   * — though edits that don't grow resource usage usually won't trip it.
   *
   * @param {string} id  instance id (ti_…)
   * @param {{ values?: object, addons?: object, name?: string,
   *           target?: object, expected_updated_at?: string }} payload
   */
  async updateInstance(id, payload = {}) {
    try {
      const response = await utsAxios.put(
        `/cluster/uts/instances/${encodeURIComponent(id)}`,
        payload
      )
      return unwrap(response)
    } catch (error) {
      const status = error?.response?.status
      const body = error?.response?.data || {}
      if (status === 409) {
        throw new ConflictError(
          body.message || body.error || 'This app was updated elsewhere',
          { current: body.current ?? body?.data?.current ?? null, ...(body || {}) }
        )
      }
      if (status === 402) {
        throw new QuotaError(body.message || body.error || 'Quota exceeded', {
          reason: body.reason || body.error || 'quota_exceeded',
          suggested_plan_id: body.suggested_plan_id ?? body?.data?.suggested_plan_id ?? null,
          ...(body.data || {})
        })
      }
      throw error
    }
  },

  /**
   * Pod list (Pods tab). Returns `[]` when the backend 404s (instance never
   * ran a preflight/runtime pipeline) so the caller can fall back to the
   * pod-summary embedded in the instance's `reconciled_branches`.
   */
  async getPods(id) {
    try {
      const response = await utsAxios.get(
        `/cluster/uts/instances/${encodeURIComponent(id)}/pods`
      )
      const data = unwrap(response)
      return Array.isArray(data) ? data : (data?.pods || [])
    } catch (error) {
      if (error?.response?.status === 404) return []
      throw error
    }
  },

  /** Snapshots list (Snapshots tab). Each snapshot id is `ts_…`. */
  async getSnapshots(id) {
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/snapshots`
    )
    return unwrap(response) || []
  },

  /** A single snapshot's detail (drawer). */
  async getSnapshot(id, snapshotId) {
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/snapshots/${encodeURIComponent(snapshotId)}`
    )
    return unwrap(response)
  },

  /**
   * Take a manual config snapshot (restore point). Gated server-side by the
   * `backup` resource at the app scope. `label` is an optional ≤120-char note.
   */
  async takeSnapshot(id, label) {
    const response = await utsAxios.post(
      `/cluster/uts/instances/${encodeURIComponent(id)}/snapshots`,
      label ? { label } : {}
    )
    return unwrap(response)
  },

  /**
   * Restore (revert) the app to a snapshot — re-deploys from the snapshot's
   * frozen values/addons and records a `rollback` snapshot. Destructive; gated
   * server-side by the `restore` resource (Full) at the app scope.
   */
  async revertSnapshot(id, snapshotId) {
    const response = await utsAxios.post(
      `/cluster/uts/instances/${encodeURIComponent(id)}/snapshots/${encodeURIComponent(snapshotId)}/revert`
    )
    return unwrap(response)
  },

  /**
   * Full preflight detail (Preflight tab). Returns `null` on 404 (no
   * preflight pipeline for this instance) so the view shows an empty state
   * rather than an error card.
   */
  async getPreflight(id) {
    try {
      const response = await utsAxios.get(
        `/cluster/uts/instances/${encodeURIComponent(id)}/preflight`
      )
      return unwrap(response)
    } catch (error) {
      if (error?.response?.status === 404) return null
      throw error
    }
  },

  /** Restart pods (lifecycle action). */
  async restartPods(id) {
    const response = await utsAxios.post(
      `/cluster/uts/instances/${encodeURIComponent(id)}/preflight/restart-pods`
    )
    return unwrap(response)
  },

  /** Retry the deploy pipeline (lifecycle action). */
  async retryPipeline(id) {
    const response = await utsAxios.post(
      `/cluster/uts/instances/${encodeURIComponent(id)}/preflight/retry-pipeline`
    )
    return unwrap(response)
  },

  /** Re-run the deploy for an existing instance. Alias of `deployInstance`. */
  async redeploy(id) {
    return this.deployInstance(id)
  },

  /**
   * Scale the instance's workload by a relative delta (+1 / −1). The backend
   * resolves the new replica count off the LIVE K8s spec.replicas, so back-to-
   * back clicks compose correctly even when the reconciled summary is stale.
   * Pass an absolute `replicas` instead to scale to a specific count. Gated
   * server-side (admin or instance owner); a 403 surfaces as an error.
   * Returns `{ kind, namespace, name, branch, from_replicas, to_replicas }`.
   * @param {string} id
   * @param {{ delta?: number, replicas?: number, branch?: string }} body
   */
  async scaleInstance(id, body = {}) {
    const response = await utsAxios.post(
      `/cluster/uts/instances/${encodeURIComponent(id)}/scale`,
      body
    )
    return unwrap(response)
  },

  /**
   * Preview what a delete will remove. `mode` is REQUIRED by the backend:
   * `full_application` (default here — whole instance) or `branch_only`
   * (also needs `branch`).
   *
   * The endpoint is origin-aware (cluster-api 2026-08-18) and returns one of
   * two shapes, tagged by `kind`:
   *   - cs-application → `{ kind:'cs-application', workload_name, config_repo,
   *     dns_records, kubernetes_resources, scope }`;
   *   - catalog stack  → `{ kind:'catalog', workload_name (= instance name),
   *     cluster, namespace, will_delete[{kind,name}], pvcs[{kind,name,
   *     will_delete}], retained[{kind,name,reason}], namespace_action,
   *     namespace_note, warnings[] }` — the UTS cascade dry-run.
   * `workload_name` is the string the typed-confirmation delete must match
   * for both kinds. `include_pvcs` (catalog only) previews the data-volume
   * choice; `mode` is ignored for catalog instances but still required.
   * @param {string} id
   * @param {{ mode?: 'full_application'|'branch_only', branch?: string, include_pvcs?: boolean }} [opts]
   */
  async deletePreview(id, { mode = 'full_application', branch, include_pvcs } = {}) {
    const params = { mode }
    if (mode === 'branch_only' && branch) params.branch = branch
    if (include_pvcs) params.include_pvcs = 'true'
    const response = await utsAxios.get(
      `/cluster/uts/instances/${encodeURIComponent(id)}/delete-preview`,
      { params }
    )
    return unwrap(response)
  },

  /**
   * Delete an instance (typed-confirmation contract). The caller must pass
   * `confirmation` matching the preview's `workload_name` (cs-application:
   * the workload name; catalog: the instance name). For catalog instances
   * `options.include_pvcs` (default false, DESTROYS DATA) and
   * `options.delete_namespace_if_empty` (backend default: only when the
   * platform created the namespace) drive the cascade.
   *
   * A partial teardown comes back as HTTP 207 with `success:false` +
   * `errors[].error_code === 'partial_delete'`; `unwrap()` throws for it and
   * the thrown Error keeps `.response` so `deleteErrorDetails()` in
   * utils/delete-shape.js can list the failed items.
   * @param {string} id
   * @param {{ mode?: string, confirmation: string, branch?: string, options?: object }} payload
   */
  async deleteInstance(id, payload = {}) {
    const body = { mode: 'full_application', ...payload }
    if (body.options && typeof body.options !== 'object') delete body.options
    const response = await utsAxios.post(
      `/cluster/uts/instances/${encodeURIComponent(id)}/delete`,
      body
    )
    return unwrap(response)
  }
}

/**
 * Connection-info client. The backend keys this by cluster + namespace
 * (NOT instance id), so callers derive those from the instance `target`.
 * Returns the per-component connection details (endpoints, services, ports,
 * connection strings) that `ConnectionModal` renders.
 *
 * Since cluster-api 2.25.45 the SERVER hides credential values here: the
 * payload carries stubs plus a `secret_masking` report, and the client renders
 * what it is given. `reveal: true` sends `?reveal=1`, the explicit and audited
 * ask for clear values — granted only to a caller who passes an
 * instance-scoped `secret:viewer` check, 403 `secret_reveal_forbidden`
 * otherwise. Never send it speculatively.
 */
export const connectionApi = {
  /**
   * `reveal` asks for clear credential values. The backend requires
   * `secret:viewer` on the instance (403 `secret_reveal_forbidden` otherwise)
   * and writes an audit row naming the keys it returned — so a reveal is a
   * deliberate, recorded act, never a page load.
   *
   * `keys` (dotted paths taken from a previous response's
   * `secret_masking.masked_keys`) narrows the reveal to those fields. The
   * modal passes ONE key when someone copies a hidden row, so pasting a
   * password does not pull every credential in the namespace into the browser;
   * it omits `keys` only for Copy all, which genuinely asks for them all and
   * should be audited as such.
   */
  async getConnectionInfo({
    cluster, namespace, workloadName, templateId, reveal = false, keys = null
  } = {}) {
    const params = {}
    if (cluster) params.cluster = cluster
    if (namespace) params.namespace = namespace
    // Passing the workload + template scopes the response to THIS app: the
    // backend then returns the per-workload `cs_application` block (ingress
    // URL, all_ingresses, internal URL) instead of only namespace-wide DNS +
    // pods. Without these the modal can only show a flat key/value dump.
    if (workloadName) params.workload_name = workloadName
    params.template_id = templateId || 'cs-application'
    if (reveal) params.reveal = 1
    if (reveal && Array.isArray(keys) && keys.length) params.keys = keys.join(',')
    const response = await utsAxios.get(
      '/cluster/app-templates/installed/connection-info',
      { params }
    )
    return unwrap(response)
  }
}

/**
 * DNS check / register for the Connection-info modal. Mirrors the cluster-api
 * dashboard's DNS Configuration actions. The backend enforces permissions, so
 * a customer without DNS rights simply gets a failed result badge.
 */
export const dnsApi = {
  async check(hostname) {
    const r = await utsAxios.get('/cluster/dns/check-hostname', {
      params: { hostname }
    })
    return unwrap(r) || {}
  },
  async register(hostname, targetIp) {
    const r = await utsAxios.post('/cluster/dns/register-hostname', {
      hostname,
      target_ip: targetIp
    })
    return unwrap(r)
  },
  /** Idempotent batch upsert (dns-manager dedupes + self-heals). */
  async registerBatch(records) {
    const r = await utsAxios.post('/cluster/dns/register-hostnames-batch', {
      records
    })
    return unwrap(r) || {}
  }
}

/**
 * vCluster isolation status — drives the suspended-trial banner on My Apps.
 * `{ provisioned, suspended, status, tier, namespace, vcluster_name }`.
 */
export const isolationApi = {
  async getStatus({ user_id, organization_id } = {}) {
    const params = {}
    if (user_id != null) params.user_id = user_id
    if (organization_id != null) params.organization_id = organization_id
    const response = await utsAxios.get('/cluster/vclusters/isolation-status', {
      params
    })
    return unwrap(response)
  }
}

export default utsAxios

// 423 organization_locked → the locked-organisation page, once per burst.
attachOrgLockedHandler(utsAxios)
